import { head, last, uniqBy } from "lodash";
import { PriceRule, PricesTypes } from "../summary/enums";
import { IPricing } from "../summary/pricing";
import { DATATEST } from "constants/integration";

interface INormalizedPricesResult {
    prices: PriceRule[];
    msrp: string;
    wholesale: string;
    discounted_wholesale: string;
}

export const normalizePrices = ({
    scope,
    product,
    currency,
    shipStart,
}: Omit<IPricing, "alignment">): INormalizedPricesResult => {
    let pricesToRender: PriceRule[] = [];
    const enablePricingPromotion = scope.brand.isPricingPromotionEnabled(scope);
    const enableStrikethrough = scope.brand.isStrikethroughEnabled(scope);
    const {
        msrp,
        minOriginalWholesale,
        originalWholesale,
        minAdjustedWholesale,
        adjustedWholesale,
        minDiscountedWholesale,
        discountedWholesale,
    } = product.getFormattedPrices(currency, { date: shipStart }, scope);

    const _originalWholesale = enablePricingPromotion && originalWholesale;
    if (enableStrikethrough) {
        pricesToRender = [
            {
                type: PricesTypes.discounted_wholesale,
                price: discountedWholesale || adjustedWholesale,
                dataTest: DATATEST.PRODUCT.PRICES.DISCOUNTED,
            },
            {
                type: PricesTypes.wholesale,
                price:
                    minOriginalWholesale > (minDiscountedWholesale || minAdjustedWholesale) ? originalWholesale : null,
                dataTest: DATATEST.PRODUCT.PRICES.WHOLESALE,
            },
        ];
    } else {
        const basePrice = adjustedWholesale || _originalWholesale;
        pricesToRender = [
            {
                type: PricesTypes.discounted_wholesale,
                price: discountedWholesale,
                dataTest: DATATEST.PRODUCT.PRICES.DISCOUNTED,
            },
            {
                type: PricesTypes.wholesale,
                price:
                    (minAdjustedWholesale || minOriginalWholesale) > minDiscountedWholesale || !minDiscountedWholesale
                        ? basePrice
                        : null,
                dataTest: DATATEST.PRODUCT.PRICES.WHOLESALE,
            },
        ];
    }

    const prices = uniqBy(
        pricesToRender.filter(({ price }) => Boolean(price)),
        "price"
    );

    return { prices, msrp, wholesale: last(prices)?.price, discounted_wholesale: head(prices)?.price };
};
